import type { Nullable } from "@msuite/utils/types";
import React, { useRef, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { useDisclosure } from "../hooks";

/** Types */
export type AsyncModalController<T = boolean> = ReturnType<typeof useAsyncModal<T>>;

export interface ConfirmOptions {
	message?: string;
	render?: React.ReactNode;
}

export function useAsyncModal<T = boolean>() {
	/** State */
	const [isPending, setIsPending] = useState<boolean>(false);

	/** Hooks */
	const { isOpen, onOpen, onClose } = useDisclosure();

	const inputRef = useRef<unknown | null>(null);
	const promiseRef = useRef<((value: T | boolean) => void) | null>(null);
	const messageRef = useRef<string | null>(null);
	const renderRef = useRef<React.ReactNode | null>(null);
	const methodsRef = useRef<UseFormReturn<any> | null>(null);

	/** Functions */
	async function confirm<U extends FieldValues>(
		methods?: Nullable<UseFormReturn<U>>,
		options?: ConfirmOptions,
	) {
		onOpen();
		setIsPending(true);

		if (options?.message) {
			messageRef.current = options?.message;
		}

		if (options?.render) {
			renderRef.current = options?.render;
		}

		if (methods) {
			methodsRef.current = methods;
		}

		const promise = new Promise<T | boolean>((resolve) => {
			promiseRef.current = resolve;
		}).finally(() => {
			promiseRef.current = null;
		});
		const response = await promise;
		return response;
	}

	async function onResolve(value: T | boolean) {
		if (value && methodsRef.current) {
			const valid = await methodsRef.current.trigger();
			if (!valid) return false;
		}

		if (promiseRef.current) {
			promiseRef.current(value);
			promiseRef.current = null;
		}
		onClose();
		setIsPending(false);
	}

	/** Return */
	return {
		confirm,
		isOpen,
		onClose,
		onResolve,
		isPending,
		inputRef,
		messageRef,
		renderRef,
	};
}
